import { Campus } from '@/modules/core/data/types/Campus';
import ChatbotQuestion from '@/modules/core/data/types/CDE/ChatbotQuestion';
import {
  CHATBOT_CAMPUSES_KEY,
  CHATBOT_CATEGORIES_KEY,
  CHATBOT_EDUCATIONAL_PROGRAM_STEPS_KEY,
  CHATBOT_EDUCATIONAL_PROGRAMS_KEY,
  CHATBOT_PERSONA_KEY,
  ChatbotContext,
} from '@/modules/core/data/types/ChatbotContext';
import { Customer } from '@/modules/core/data/types/Customer';
import { EducationalProgram } from '@/modules/core/data/types/EducationalProgram';
import { EducationalProgramStep } from '@/modules/core/data/types/EducationalProgramStep';
import { StudentSolution } from '@/modules/core/data/types/StudentSolution';
import { StudentSolutionsCategory } from '@/modules/core/data/types/StudentSolutionsCategory';
import { ReactSelectRefLabelOption } from '@/modules/core/reactSelect/types/ReactSelect';
import {
  convertCampusesAsSelectOptions,
  filterCampusesByProgramSteps,
} from '@/modules/solutions/campus';
import {
  convertCategoriesAsSelectOptions,
  filterCategoriesBySelectedCampusesAndProgramSteps,
} from '@/modules/solutions/category';
import { TFunction } from 'i18next';
import map from 'lodash.map';
import size from 'lodash.size';
import { RESPONSE_DISPLAY_TYPE } from '../../chatbot/types/Chatbot';
import { convertStepsToStepsGroupedByProgramsAsSelectOptions } from '../../solutions/educationalProgramStep';

/**
 * All questions that might be asked by the chatbot.
 */
export type ChatbotQuestions = {
  personaQuestion: ChatbotQuestion;
  programsQuestion: ChatbotQuestion;
  programStepsQuestion: ChatbotQuestion;
  campusesQuestion: ChatbotQuestion;
  categoryQuestion: ChatbotQuestion;
}

/**
 * Compute all questions that might be asked by the chatbot, and their configuration (choices, i18n, etc.)
 */
export const getQuestionsToAsk = (context: ChatbotContext, t: TFunction, customer: Customer, allEducationalPrograms: EducationalProgram[], allEducationalProgramSteps: EducationalProgramStep[], allCampuses: Campus[], allStudentSolutionsCategories: StudentSolutionsCategory[], allStudentSolutions: StudentSolution[]): any => {
  const personaQuestion: ChatbotQuestion = {
    label: t('chatbotPage.dialog.question.persona', 'Pour commencer, faisons connaissance&nbsp;! Vous êtes&nbsp;:'),
    choices: [
      {
        value: 'candidate',
        label: t('chatbotPage.dialog.question.personaChoices.candidate', 'Un candidat'),
      },
      {
        value: 'student',
        label: t('chatbotPage.dialog.question.personaChoices.student', `Un étudiant de <b>{{label}}</b>`, {
          label: customer?.labelShort,
        }),
      },
      {
        value: 'parent',
        label: t('chatbotPage.dialog.question.personaChoices.parent', 'Un parent'),
      },
      {
        value: 'other',
        label: t('chatbotPage.dialog.question.personaChoices.other', 'Autre'),
      },
    ],
    order: 1,
    responseDisplayType: RESPONSE_DISPLAY_TYPE.SINGLE_CHOICE_INPUT,
    ref: CHATBOT_PERSONA_KEY,
    hideIfNoChoice: true,
  };
  const programsQuestion: ChatbotQuestion = {
    label: t('chatbotPage.dialog.question.educationalProgram', 'Quel est le programme qui vous intéresse&nbsp;?'),
    choices: map(allEducationalPrograms, (choice): ReactSelectRefLabelOption => {
      return {
        ref: choice.ref,
        label: choice.label,
      };
    }),
    order: 1,
    responseDisplayType: RESPONSE_DISPLAY_TYPE.MULTI_SELECT_INPUT,
    ref: CHATBOT_EDUCATIONAL_PROGRAMS_KEY,
    hideIfNoChoice: true,
  };
  const programStepsQuestion: ChatbotQuestion = {
    label: t('chatbotPage.dialog.question.educationalProgramStep', 'Et plus précisément&nbsp;...', { count: size(context?.educationalPrograms) }),
    choices: convertStepsToStepsGroupedByProgramsAsSelectOptions(allEducationalProgramSteps, context.educationalPrograms),
    order: 2,
    responseDisplayType: RESPONSE_DISPLAY_TYPE.MULTI_SELECT_INPUT,
    ref: CHATBOT_EDUCATIONAL_PROGRAM_STEPS_KEY,
    hideIfNoChoice: true,
  };
  const campusesQuestion: ChatbotQuestion = {
    label: t('chatbotPage.dialog.question.campus', 'Dans quel campus souhaitez-vous étudier&nbsp;?'),
    choices: convertCampusesAsSelectOptions(filterCampusesByProgramSteps(allCampuses, allEducationalProgramSteps, context.educationalProgramSteps)),
    order: 3,
    responseDisplayType: RESPONSE_DISPLAY_TYPE.SINGLE_CHOICE_INPUT,
    ref: CHATBOT_CAMPUSES_KEY,
    hideIfNoChoice: true,
  };
  const categoryQuestion: ChatbotQuestion = {
    label: t('chatbotPage.dialog.question.category', 'Quelle catégorie de solutions souhaites-tu consulter en premier&nbsp;?<br /> Tu pourras bien sûr explorer toutes les autres catégories sur la page suivante&nbsp;😉'),
    choices: [
      { ref: undefined, label: t('chatbotPage.dialog.choice.seeAllCategories', 'Voir toutes les catégories') }, // Dynamically add an "All" option that will be ignored and won't  apply a filter
      ...convertCategoriesAsSelectOptions(filterCategoriesBySelectedCampusesAndProgramSteps(allStudentSolutionsCategories, allCampuses, allEducationalProgramSteps, context)),
    ],
    order: 4,
    responseDisplayType: RESPONSE_DISPLAY_TYPE.SINGLE_CHOICE_INPUT,
    ref: CHATBOT_CATEGORIES_KEY,
    hideIfNoChoice: true,
  };

  return {
    personaQuestion,
    programsQuestion,
    programStepsQuestion,
    campusesQuestion,
    categoryQuestion,
  };
};

export default getQuestionsToAsk;
